import { useRef } from "react";
import { fmtTime, CATEGORY_COLORS } from "@/lib/api";

export const Timeline = ({ duration, current, range, setRange, highlights, activeId, onSeek, onPickHighlight }) => {
  const ref = useRef(null);
  const pct = (t) => `${(Math.max(0, Math.min(t, duration)) / (duration || 1)) * 100}%`;
  const timeAt = (clientX) => {
    const r = ref.current.getBoundingClientRect();
    return Math.max(0, Math.min(duration, ((clientX - r.left) / r.width) * duration));
  };

  const drag = (which) => (e) => {
    e.stopPropagation();
    e.preventDefault();
    const move = (ev) => {
      const t = timeAt(ev.clientX);
      setRange((r) => (which === "start" ? { ...r, start: Math.min(t, r.end - 0.5) } : { ...r, end: Math.max(t, r.start + 0.5) }));
    };
    const up = () => { window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up); };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };

  const ticks = Array.from({ length: 11 }, (_, i) => (duration / 10) * i);

  return (
    <div className="select-none" data-testid="editor-timeline">
      <div className="relative h-24 rounded-lg bg-[#070912] border border-white/10 cursor-crosshair overflow-visible" ref={ref} onPointerDown={(e) => onSeek(timeAt(e.clientX))}>
        <div className="absolute inset-0 opacity-30" style={{ backgroundImage: "repeating-linear-gradient(90deg, rgba(255,255,255,0.08) 0 1px, transparent 1px 8px)" }} />
        {highlights.map((h) => (
          <button key={h.id} data-testid="timeline-ai-marker" title={`${h.label} · ${h.score}`}
            onPointerDown={(e) => { e.stopPropagation(); onPickHighlight(h); }}
            className={`absolute top-2 h-7 rounded-md text-[10px] font-mono font-semibold text-white px-1.5 overflow-hidden whitespace-nowrap transition-[box-shadow,opacity] ${activeId === h.id ? "ring-2 ring-white shadow-[0_0_16px_rgba(58,215,255,0.8)]" : "opacity-80 hover:opacity-100"}`}
            style={{ left: pct(h.start), width: pct(h.end - h.start), background: `linear-gradient(90deg, ${CATEGORY_COLORS[h.category] || "#a746ff"}, #2f7bffcc)` }}>
            {h.score}
          </button>
        ))}
        <div className="absolute top-11 bottom-2 bg-[#2f7bff]/20 border-y-2 border-[#3ad7ff]" style={{ left: pct(range.start), width: `calc(${pct(range.end)} - ${pct(range.start)})` }} data-testid="timeline-selection" />
        <div data-testid="timeline-start-handle" onPointerDown={drag("start")} className="absolute top-9 bottom-0 w-3 -ml-1.5 bg-[#3ad7ff] rounded cursor-ew-resize shadow-[0_0_12px_rgba(58,215,255,0.9)] z-10" style={{ left: pct(range.start) }} />
        <div data-testid="timeline-end-handle" onPointerDown={drag("end")} className="absolute top-9 bottom-0 w-3 -ml-1.5 bg-[#a746ff] rounded cursor-ew-resize shadow-[0_0_12px_rgba(167,70,255,0.9)] z-10" style={{ left: pct(range.end) }} />
        <div className="absolute -top-1 -bottom-1 w-0.5 bg-white z-20 pointer-events-none" style={{ left: pct(current) }}>
          <div className="w-2.5 h-2.5 bg-white rotate-45 -ml-[4px] -mt-1" />
        </div>
      </div>
      <div className="relative h-5 mt-1.5">
        {ticks.map((t, i) => (
          <span key={i} className="absolute font-mono text-[10px] text-slate-500 -translate-x-1/2" style={{ left: pct(t) }}>{fmtTime(t)}</span>
        ))}
      </div>
    </div>
  );
};
